import type { CategoryShort } from "~/types/category";
import { AllowedLocales } from "~/types/i18n";
import { generateStoreCode } from "~/modules/i18n-extend/helpers";
import { ResolvedMagentoRoute } from "~/modules/vsf-sdk/types/ResolvedMagentoRoute";

export function useCategoryResolver() {
  const { store, locale } = useI18nExtend();
  const { searchCategory } = useCategorySearch();
  const { resolve, isRouteCategory } = useMagentoRoute();

  async function resolveCategory<T extends CategoryShort = CategoryShort>(
    urlPath: string,
    projection?: string,
    locale?: AllowedLocales,
  ): Promise<(T & ResolvedMagentoRoute) | undefined> {
    projection = projection || "CategoryShortProj";

    const resolvedRoute = await resolve({
      url: urlPath,
      queryName: "resolveCategory",
      store: locale ? generateStoreCode(store.value.code, locale) : "",
      metadata: {
        projection,
      },
    });

    if (!resolvedRoute || !isRouteCategory(resolvedRoute)) return undefined;

    return resolvedRoute as T & ResolvedMagentoRoute;
  }

  /**
   * Resolves a category based on the provided URL path in multiple locales.
   * It helps when we have some urlPath, but we are not sure in what locales it is
   * Return category in the CURRENT locale
   * WARNING: This method sends several requests
   *
   * @param {string} urlPath - The URL path of the category.
   * @return {Promise<CategoryShort | undefined>} - A promise that resolves to the resolved category or undefined if not found.
   */
  async function resolveCategoryByUrlPathMultiLocale(
    urlPath: string,
  ): Promise<CategoryShort | undefined> {
    for (const storeAllowedLocale of store.value.locales) {
      if (storeAllowedLocale === locale.value) {
        continue;
      }

      const categories = await resolveCategoriesByUrlPaths(
        [urlPath],
        false,
        storeAllowedLocale,
      );

      if (categories.length) {
        const resolvedCategoryByUid = await resolveCategoriesByUIds([
          categories[0].uid,
        ]);
        if (resolvedCategoryByUid.length) {
          return resolvedCategoryByUid[0];
        }
      }
    }

    return undefined;
  }

  /**
   * Resolves categories by their URL paths.
   *
   * @param {string[]} urlPaths - An array of URL paths for the categories.
   * @param strict
   * @param locale
   * @param projection
   * @returns {Promise<CategoryShort[]>} - A promise that resolves to an array of CategoryShort objects.
   * @throws {Error} - Throws an error if some categories are not resolved.
   */
  async function resolveCategoriesByUrlPaths<
    T extends CategoryShort = CategoryShort,
  >(
    urlPaths: string[],
    strict: boolean = true,
    locale?: AllowedLocales,
    projection?: string,
  ): Promise<T[]> {
    projection = projection || "CategoryShortProj";
    const urlKeys = urlPaths.map((_, index) =>
      urlPaths.slice(0, index + 1).join("/"),
    );
    const categories = await searchCategory<T>(
      {
        url_path: {
          in: urlKeys,
        },
      },
      {
        customQuery: {
          categorySearch: "categorySearch",
          metadata: {
            projection,
          },
        },
        customHeaders: {
          Store: locale ? generateStoreCode(store.value.code, locale) : "",
        },
      },
    );

    if (categories.length !== urlKeys.length && strict) {
      throw new Error(`Some categories are not resolved`);
    }

    return (categories || []).sort(
      (x, y) => urlKeys.indexOf(x.url_path) - urlKeys.indexOf(y.url_path),
    );
  }

  async function resolveCategoriesByUIds(
    uIds: string[],
    strict: boolean = true,
  ): Promise<CategoryShort[]> {
    const categories: CategoryShort[] = await searchCategory({
      category_uid: {
        in: uIds,
      },
    });

    if (categories.length !== uIds.length && strict) {
      throw new Error(`Some categories are not resolved`);
    }

    return (categories || []).sort(
      (x, y) => uIds.indexOf(x.uid) - uIds.indexOf(y.uid),
    );
  }

  return {
    resolveCategory,
    resolveCategoryByUrlPathMultiLocale,
  };
}
